// Чистая логика экранов тренировок: подписи, свёртка подходов, модель редактора «задним числом».

import { formatDuration, formatNumber, parseDecimal } from '@/lib/format'

import type {
  Equipment,
  PrFlags,
  Session,
  SessionInput,
  SetInput,
  Template,
  Tracking,
  WorkoutSet,
} from './api'

export const EQUIPMENT_LABELS: Record<Equipment, string> = {
  barbell: 'Штанга',
  dumbbell: 'Гантели',
  smith: 'Смит',
  machine: 'Тренажёр',
  cable: 'Блок',
  bodyweight: 'Свой вес',
  kettlebell: 'Гиря',
  band: 'Резина',
  other: 'Другое',
}

export const TRACKING_LABELS: Record<Tracking, string> = {
  weight_reps: 'Вес × повторы',
  bodyweight_reps: 'Свой вес × повторы',
  time: 'Время',
  distance: 'Дистанция',
}

export const PR_LABELS: Record<keyof PrFlags, string> = {
  weight: 'вес',
  e1rm: '1ПМ',
  volume: 'объём',
  reps: 'повторы',
  duration: 'время',
  distance: 'дистанция',
}

/** Какие поля подхода показывать для типа учёта. Дистанция (дорожка, гребля) — ещё и с временем. */
export function setFields(tracking: Tracking): { weight: boolean; reps: boolean; duration: boolean; distance: boolean } {
  return {
    weight: tracking === 'weight_reps' || tracking === 'bodyweight_reps',
    reps: tracking === 'weight_reps' || tracking === 'bodyweight_reps',
    duration: tracking === 'time' || tracking === 'distance',
    distance: tracking === 'distance',
  }
}

/** Время подхода с секундами: «45 с», «1 мин 30 с», «20 мин», «1 ч 05 мин». */
function setDuration(seconds: number | null): string {
  if (seconds === null || seconds % 60 === 0 || seconds > 3600) return formatDuration(seconds)
  return seconds < 60 ? `${seconds} с` : `${Math.floor(seconds / 60)} мин ${seconds % 60} с`
}

type SetValues = Pick<WorkoutSet, 'reps' | 'weight_kg' | 'duration_s' | 'distance_m'>

/** Подход одной строкой: «80 кг × 8», «× 12», «+10 кг × 8», «1 мин 30 с», «500 м · 20 мин». */
export function formatSet(set: SetValues, tracking: Tracking): string {
  if (tracking === 'time') return setDuration(set.duration_s)
  if (tracking === 'distance') {
    const parts = [
      set.distance_m ? `${formatNumber(set.distance_m, { digits: 0 })} м` : null,
      set.duration_s ? setDuration(set.duration_s) : null,
    ].filter(Boolean)
    return parts.length ? parts.join(' · ') : '—'
  }
  // Пустой подход (ещё не заполнен) — «—», а не «× 0».
  if (!set.weight_kg && set.reps === null) return '—'
  if (!set.weight_kg) return `× ${set.reps}`
  const prefix = tracking === 'bodyweight_reps' ? '+' : ''
  const weight = `${prefix}${formatNumber(set.weight_kg, { digits: 2 })} кг`
  return set.reps === null ? weight : `${weight} × ${set.reps}`
}

/** Свёртка рабочих подходов: «3 × 8 × 80 кг» для одинаковых, иначе через запятую. */
export function summarizeSets(sets: SetValues[], tracking: Tracking): string {
  if (sets.length === 0) return '—'
  const first = sets[0]!
  const same = sets.every(
    (s) =>
      s.reps === first.reps &&
      s.weight_kg === first.weight_kg &&
      s.duration_s === first.duration_s &&
      s.distance_m === first.distance_m,
  )
  if (same && sets.length > 1 && (tracking === 'weight_reps' || tracking === 'bodyweight_reps')) {
    const weight = first.weight_kg ? ` × ${formatNumber(first.weight_kg, { digits: 2 })} кг` : ''
    return `${sets.length} × ${first.reps ?? 0}${weight}`
  }
  if (same && sets.length > 1) return `${sets.length} × ${formatSet(first, tracking)}`
  return sets.map((s) => formatSet(s, tracking)).join(', ')
}

export type ExerciseGroup = {
  exercise_id: string
  position: number
  name: string
  tracking: Tracking
  archived: boolean
  sets: WorkoutSet[]
}

/** Подходы тренировки по упражнениям в порядке выполнения. */
export function groupSets(session: Session): ExerciseGroup[] {
  return session.exercises.map((ex) => ({
    ...ex,
    sets: session.sets
      .filter((s) => s.exercise_id === ex.exercise_id && s.exercise_position === ex.position)
      .sort((a, b) => a.set_index - b.set_index),
  }))
}

// --- Редактор тренировки (ввод задним числом и правка) -------------------------------------

export type EditorSet = {
  id: string
  weight: string
  reps: string
  duration: string
  distance: string
  is_warmup: boolean
  completed: boolean
  rest_s: number | null
}

export type EditorExercise = {
  key: string
  exercise_id: string
  name: string
  tracking: Tracking
  sets: EditorSet[]
}

export type EditorState = {
  id: string
  date: string
  start: string
  end: string
  template_id: string | null
  notes: string
  exercises: EditorExercise[]
}

const pad = (n: number) => String(n).padStart(2, '0')

export function newId(): string {
  return crypto.randomUUID()
}

const numText = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value).replace('.', ','))

/** Время «ЧЧ:ММ» из длительности в секундах (для поля «мин:сек»). */
function durationText(seconds: number | null | undefined): string {
  if (!seconds) return ''
  return `${Math.floor(seconds / 60)}:${pad(seconds % 60)}`
}

/** Длительность из поля ввода: «1:30» → 90 с, голое число — секунды. */
export function parseDuration(text: string): number | null {
  const t = text.trim()
  if (!t) return null
  const match = /^(\d+):(\d{1,2})$/.exec(t)
  if (match) return Number(match[1]) * 60 + Number(match[2])
  const n = parseDecimal(t)
  return n === null ? null : Math.round(n)
}

export function emptySet(): EditorSet {
  return { id: newId(), weight: '', reps: '', duration: '', distance: '', is_warmup: false, completed: true, rest_s: null }
}

/** Новый подход — копия предыдущего (без отметки «разминка»). */
export function copySet(prev: EditorSet | undefined): EditorSet {
  return prev ? { ...prev, id: newId(), is_warmup: false } : emptySet()
}

function localParts(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

/** Дата 'YYYY-MM-DD' и время 'ЧЧ:ММ' в местном поясе → ISO-время. */
export function combineDateTime(date: string, time: string): string | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  const tm = /^(\d{1,2}):(\d{2})$/.exec(time)
  if (!dm || !tm) return null
  const d = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]))
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export function emptyEditor(now: Date = new Date()): EditorState {
  const start = new Date(now.getTime() - 60 * 60 * 1000)
  const startParts = localParts(start.toISOString())
  return {
    id: newId(),
    date: startParts.date,
    start: startParts.time,
    end: localParts(now.toISOString()).time,
    template_id: null,
    notes: '',
    exercises: [],
  }
}

export function editorFromSession(session: Session): EditorState {
  const start = localParts(session.started_at)
  return {
    id: session.id,
    date: start.date,
    start: start.time,
    end: session.ended_at ? localParts(session.ended_at).time : '',
    template_id: session.template_id,
    notes: session.notes ?? '',
    exercises: groupSets(session).map((g) => ({
      key: `${g.exercise_id}:${g.position}`,
      exercise_id: g.exercise_id,
      name: g.name,
      tracking: g.tracking,
      sets: g.sets.map((s) => ({
        id: s.id,
        weight: numText(s.weight_kg),
        reps: numText(s.reps),
        duration: durationText(s.duration_s),
        distance: numText(s.distance_m),
        is_warmup: s.is_warmup,
        completed: s.completed,
        rest_s: s.rest_s,
      })),
    })),
  }
}

/** Упражнения шаблона с целевыми подходами — заготовка для ввода. */
export function exercisesFromTemplate(template: Template): EditorExercise[] {
  return template.exercises
    .filter((e) => !e.exercise_archived)
    .map((e) => ({
      key: newId(),
      exercise_id: e.exercise_id,
      name: e.exercise_name,
      tracking: e.tracking,
      sets: (e.target_sets.length ? e.target_sets : [null]).map((t) => ({
        ...emptySet(),
        weight: numText(t?.weight_kg),
        reps: numText(t?.reps),
        duration: durationText(t?.duration_s),
        distance: numText(t?.distance_m),
        rest_s: t?.rest_s ?? null,
      })),
    }))
}

export type EditorPayload = {
  session: SessionInput
  sets: { id: string; data: SetInput }[]
}

export type EditorErrors = Partial<Record<'date' | 'start' | 'end' | 'sets', string>>

function isEmptySet(s: EditorSet): boolean {
  return !s.weight.trim() && !s.reps.trim() && !s.duration.trim() && !s.distance.trim()
}

/** Состояние редактора → запросы PUT тренировки и подходов. Пустые строки пропускаются. */
export function buildPayload(state: EditorState): { payload: EditorPayload | null; errors: EditorErrors } {
  const errors: EditorErrors = {}
  const startedAt = combineDateTime(state.date, state.start)
  if (!state.date) errors.date = 'Укажи дату'
  else if (!startedAt) errors.start = 'Укажи время начала'
  let endedAt: string | null = null
  if (state.end.trim()) {
    endedAt = combineDateTime(state.date, state.end)
    if (!endedAt) errors.end = 'Время в формате ЧЧ:ММ'
    else if (startedAt && endedAt < startedAt) {
      // Закончил после полуночи — окончание на следующий день.
      endedAt = new Date(new Date(endedAt).getTime() + 24 * 3600 * 1000).toISOString()
    }
  }
  const sets: EditorPayload['sets'] = []
  state.exercises.forEach((ex, position) => {
    let index = 0
    for (const s of ex.sets) {
      if (isEmptySet(s)) continue
      const reps = s.reps.trim() ? parseDecimal(s.reps) : null
      const weight = s.weight.trim() ? parseDecimal(s.weight) : null
      const duration = parseDuration(s.duration)
      const distance = s.distance.trim() ? parseDecimal(s.distance) : null
      if ((s.reps.trim() && reps === null) || (s.weight.trim() && weight === null) || (s.distance.trim() && distance === null)) {
        errors.sets = `Проверь числа в упражнении «${ex.name}»`
        continue
      }
      sets.push({
        id: s.id,
        data: {
          exercise_id: ex.exercise_id,
          exercise_position: position,
          set_index: index,
          reps: reps === null ? null : Math.round(reps),
          weight_kg: weight,
          duration_s: duration,
          distance_m: distance,
          is_warmup: s.is_warmup,
          completed: s.completed,
          completed_at: s.completed ? (endedAt ?? startedAt) : null,
          rest_s: s.rest_s,
        },
      })
      index += 1
    }
  })
  if (Object.keys(errors).length > 0 || !startedAt) return { payload: null, errors }
  return {
    payload: {
      session: { started_at: startedAt, ended_at: endedAt, template_id: state.template_id, notes: state.notes.trim() || null },
      sets,
    },
    errors,
  }
}

/** Подходы, которые были на сервере, но исчезли из редактора, — их надо удалить. */
export function removedSetIds(original: Session | null | undefined, payload: EditorPayload): string[] {
  if (!original) return []
  const kept = new Set(payload.sets.map((s) => s.id))
  return original.sets.filter((s) => !kept.has(s.id)).map((s) => s.id)
}
